/* Copyright 2023 (Unpublished) Verto Inc. */

import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { expand, scan } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { WorkflowV2, WorkflowV2Interface } from '@root/classes/workflow_v2';
import { SearchParamInterface } from '@root//classes/search_param';
import { Workflow, WorkflowInterface } from '@root/classes/workflow';
import { LinksInterface } from '@root/classes/links';
import { TriggerInterface } from '@root/classes/trigger';
import { WorkflowQuery } from '@root/workflows/workflow-query';

@Injectable({
  providedIn: 'root',
})
export class WorkflowService {
  workflow: Workflow;

  constructor(private http: HttpClient) {}

  getWorkflows(
    page: number = 0,
    limit: number = 10,
    queryParams: WorkflowQuery = {},
    campaign_id?: string
  ): Observable<{
    total: number;
    workflows: WorkflowV2Interface[];
  }> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());
    if (campaign_id !== undefined) {
      params = params.set('campaign_id', campaign_id);
    }
    if (!!queryParams.workflow_id) {
      params = params.set('workflow_id', queryParams.workflow_id);
    }
    if (!!queryParams.workflow_name) {
      params = params.set('workflow_name', queryParams.workflow_name);
    }
    if (!!queryParams.trigger_name) {
      params = params.set('trigger_name', queryParams.trigger_name);
    }
    if (!!queryParams.trigger_type) {
      params = params.set('trigger_type', queryParams.trigger_type);
    }
    if (!!queryParams.show_error_handlers) {
      params = params.set(
        'show_error_handlers',
        queryParams.show_error_handlers
      );
    }
    return this.http.get<{ total: number; workflows: WorkflowV2Interface[] }>(
      environment.settings.backendUrl + '/api/admin/v2/workflows',
      { params }
    );
  }

  getWorkflow(id: string): Observable<WorkflowV2Interface> {
    return this.http.get<WorkflowV2Interface>(
      environment.settings.backendUrl + '/api/admin/v2/workflows/' + id
    );
  }

  getAllWorkflows(): Observable<WorkflowV2Interface[]> {
    const params = new HttpParams().set('page', '0').set('limit', '50');
    return this.http
      .get<{ links: LinksInterface; workflows: WorkflowV2Interface[] }>(
        environment.settings.backendUrl + '/api/admin/v2/workflows',
        { params }
      )
      .pipe(
        expand((res) =>
          res.links.next !== ''
            ? this.http.get<{
                links: LinksInterface;
                workflows: WorkflowV2Interface[];
              }>(environment.settings.backendUrl + res.links.next)
            : EMPTY
        ),
        scan(
          (acc, data) => [...acc, ...data.workflows],
          new Array<WorkflowV2Interface>()
        )
      );
  }

  triggerWorkflow(
    env: object,
    workflow_id: string
  ): Observable<TriggerInterface> {
    let body = {
      arguments: env,
      workflow_id: workflow_id,
      trigger_name: 'None',
    };
    return this.http.patch<TriggerInterface>(
      environment.settings.backendUrl + '/triggers/http',
      body
    );
  }

  getSearchParams(): Observable<{ search_params: SearchParamInterface[] }> {
    return this.http.get<{ search_params: SearchParamInterface[] }>(
      environment.settings.backendUrl + '/api/admin/v2/workflows/search_params'
    );
  }

  createWorkflow(workflow: object): Observable<WorkflowV2Interface> {
    return this.http.post<WorkflowV2Interface>(
      environment.settings.backendUrl + '/api/admin/v2/workflows',
      workflow
    );
  }

  updateWorkflow(id: string, workflow: object): Observable<WorkflowInterface> {
    return this.http.patch<WorkflowInterface>(
      environment.settings.backendUrl + '/api/admin/v2/workflows/' + id,
      workflow
    );
  }

  deleteWorkflow(id: string): Observable<any> {
    return this.http.delete<any>(
      environment.settings.backendUrl + '/api/admin/v2/workflows/' + id
    );
  }
}
